import { Dialog, Portal, UseDialogProps, useDialog } from "@ark-ui/react";
import { DEFAULT_LOCALE, DELETION_FLAG, type GroupKey } from "@packages/core";
import { useEffect, useState } from "react";
import { type Merge } from "type-fest";

import { ButtonStyle, DialogStyle, TypographyStyle } from "#components/common";
import { useBedInfiniteQuery, useGroupQuery, useTranslate } from "#services";
import { css, cx } from "#styled-system/css";

export type GroupSelectorProperties = Merge<
  UseDialogProps,
  {
    options?: ReadonlyArray<GroupKey>;
    defaultValue?: ReadonlyArray<GroupKey>;
    onCancel?: { (): void };
    onChange?: { (value: ReadonlyArray<GroupKey>): void };
  }
>;
export function GroupSelector({
  defaultValue = [],
  onCancel,
  onChange,
  options = [],
  ...properties
}: GroupSelectorProperties) {
  const { open } = properties;

  const dialog = useDialog(properties);
  const translate = useTranslate();

  const styles = DialogStyle.raw();

  const [internalValue, setInternalValue] = useState(defaultValue);

  useEffect(
    function () {
      // ダイアログが開かれた時に内部値を既定値にリセットする。
      if (open) {
        setInternalValue(defaultValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );

  return (
    <Dialog.RootProvider value={dialog}>
      <Portal>
        <Dialog.Backdrop className={css(styles.backdrop)} />
        <Dialog.Positioner className={css(styles.positioner)}>
          <Dialog.Content
            className={css(styles.content, {
              maxHeight: "calc(100dvh - 96px)",
              maxWidth: "480px",
            })}
          >
            <h5 className={css(styles.title)}>{translate("%GroupSelector.0%")}</h5>
            <p
              className={css(styles.description, {
                marginTop: "8px",
              })}
            >
              {translate("%GroupSelector.1%")}
            </p>

            <div
              className={css({
                backgroundColor: "primary-foreground",
                borderRadius: "6px",
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                marginTop: "16px",
                minHeight: 0,
                padding: "16px",
              })}
            >
              <h3
                className={TypographyStyle({
                  ellipsis: true,
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {translate("%Group.0%")}
              </h3>

              <div
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginTop: "16px",
                })}
              >
                {options.map(function (option) {
                  const checked = internalValue.includes(option);
                  return (
                    <Option
                      checked={checked}
                      key={option}
                      onChange={function (option) {
                        setInternalValue(function (internalValue) {
                          return internalValue.includes(option)
                            ? internalValue.filter(function (value) {
                                return value !== option;
                              })
                            : [...internalValue, option].sort();
                        });
                      }}
                      option={option}
                    />
                  );
                })}
              </div>
            </div>

            <div
              className={css({
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
                marginTop: "16px",
                width: "stretch",
              })}
            >
              <Dialog.CloseTrigger
                className={ButtonStyle({
                  appearance: "outlined",
                  borderRadius: "4px",
                  color: "outlined",
                  fontSize: "14px",
                  padding: "10px 16px",
                })}
                onClick={function () {
                  onCancel?.();
                }}
              >
                {translate("%Cancel.0%")}
              </Dialog.CloseTrigger>

              <button
                className={ButtonStyle({
                  appearance: "contained",
                  borderRadius: "4px",
                  color: "primary",
                  fontSize: "14px",
                  padding: "10px 16px",
                })}
                onClick={function () {
                  onChange?.(internalValue);
                }}
              >
                {translate("%Save.0%")}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.RootProvider>
  );
}

function Option({
  checked,
  className,
  onChange,
  option,
  ...properties
}: Merge<
  React.ComponentProps<"label">,
  {
    checked?: boolean;
    onChange?: { (option: GroupKey, event: React.ChangeEvent<HTMLInputElement>): void };
    option: GroupKey;
  }
>) {
  const group = useGroupQuery(option);
  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "groupKey-deleted",
    condition: [
      ["groupKey", "=", option],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const translate = useTranslate();

  return (
    <label
      {...properties}
      className={cx(
        css({
          alignItems: "center",
          display: "flex",
          cursor: "pointer",
          gap: "8px",
        }),
        className,
      )}
    >
      <input
        checked={checked}
        className={css({ cursor: "pointer", height: "16px", width: "16px" })}
        onChange={function (event) {
          onChange?.(option, event);
        }}
        type="checkbox"
      />
      <p
        className={css(TypographyStyle.raw({ ellipsis: true, fontSize: "14px" }), {
          lineHeight: "16px",
        })}
      >
        {group.data?.name}
        &nbsp;(
        {translate("%NumberOfBeds.1%", {
          $: {
            numberOfBeds:
              bedInfiniteQuery.data?.pages
                .flatMap(function (page) {
                  return page.beds;
                })
                .length.toLocaleString(DEFAULT_LOCALE) ?? "0",
          },
        })}
        )
      </p>
    </label>
  );
}
